import React, { useState } from 'react';
import { Loader2, FileText, Receipt, ChevronDown, CheckCircle2 } from 'lucide-react';
import { exportQuotePdf } from '../lib/api';

/**
 * ExportPanel - sits directly under the calculator outputs on the home and
 * /quote pages.  Two tabs:
 *   1. "Field Spec Report" (default) - engineering only, no pricing
 *   2. "Estimated Quote"             - indicative pricing + GST/PST totals
 *
 * Only `project_name` is required.  Everything else (company, contact,
 * email, phone, location, notes) is behind a collapsed toggle so a visitor
 * can hit Generate in a single click with just a project name.
 *
 * On success the PDF streams to the browser via blob download. A persistent
 * confirmation toast renders inline with the issued quote number.
 */
export const ExportPanel = ({ calcMode, inputs, derived, matType = 'access' }) => {
    const [mode, setMode] = useState('spec');
    const [projectName, setProjectName] = useState('');
    const [includeContact, setIncludeContact] = useState(false);
    const [company, setCompany] = useState('');
    const [contactName, setContactName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [location, setLocation] = useState('');
    const [notes, setNotes] = useState('');
    const [status, setStatus] = useState('idle'); // idle | submitting | done | error
    const [result, setResult] = useState(null);
    const [errMsg, setErrMsg] = useState('');

    const reset = () => {
        setStatus('idle'); setResult(null); setErrMsg('');
        setProjectName(''); setCompany(''); setContactName('');
        setEmail(''); setPhone(''); setLocation(''); setNotes('');
        setIncludeContact(false);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!projectName.trim()) {
            setErrMsg('Project name is required.');
            setStatus('error');
            return;
        }
        setStatus('submitting'); setErrMsg('');
        try {
            const payload = {
                mode,
                inputs: {
                    calc_mode: calcMode,
                    mat_type: matType,
                    ...(calcMode === 'access'
                        ? { distance_km: inputs.distance_km, corners: inputs.corners }
                        : {
                            length_ft: inputs.length_ft,
                            width_ft: inputs.width_ft,
                            waste_factor: !!inputs.waste_factor,
                        }),
                    mat_dimensions: { length_ft: 14, width_ft: 8, thickness_in: 5 },
                },
                derived: {
                    total_mats: Math.max(0, Math.round(derived.total_mats)),
                    surface_sqft: Math.max(0, Math.round(derived.surface_sqft)),
                    truckloads: Math.max(0, Math.round(derived.truckloads)),
                    ...(derived.linear_ft ? { linear_ft: Math.round(derived.linear_ft) } : {}),
                    ...(derived.perimeter_ft ? { perimeter_ft: Math.round(derived.perimeter_ft) } : {}),
                },
                customer: {
                    project_name: projectName.trim(),
                    ...(includeContact
                        ? {
                            company: company.trim() || undefined,
                            contact_name: contactName.trim() || undefined,
                            email: email.trim() || undefined,
                            phone: phone.trim() || undefined,
                            location: location.trim() || undefined,
                            notes: notes.trim() || undefined,
                          }
                        : {}),
                },
            };
            const out = await exportQuotePdf(payload);
            setResult(out);
            setStatus('done');
        } catch (err) {
            const msg = err?.response?.data?.detail || err?.message || 'Could not generate PDF.';
            setErrMsg(typeof msg === 'string' ? msg : 'Could not generate PDF.');
            setStatus('error');
        }
    };

    const modeCopy = mode === 'spec'
        ? 'Free engineering spec - no pricing.'
        : 'Indicative estimate - final pricing confirmed by Centurion sales.';

    if (status === 'done' && result) {
        return (
            <div
                data-testid="export-success"
                className="border border-brand-gold/40 bg-brand-surface p-6 md:p-8"
            >
                <div className="flex items-start gap-3">
                    <CheckCircle2 className="mt-1 text-brand-gold" size={20} />
                    <div className="min-w-0 flex-1">
                        <p className="font-display text-sm font-bold uppercase tracking-[0.16em] text-brand-gold">
                            PDF generated
                        </p>
                        <p className="mt-2 font-sans text-sm text-brand-text">
                            Quote <strong className="text-brand-text">{result.quoteNumber}</strong>{' '}
                            sent to your downloads.
                        </p>
                        <p className="mt-1 break-all font-sans text-xs text-brand-muted">
                            {result.filename}
                        </p>
                        <button
                            type="button"
                            onClick={reset}
                            data-testid="export-reset"
                            className="mt-5 inline-flex items-center gap-1 font-display text-xs font-bold uppercase tracking-[0.16em] text-brand-blue hover:text-brand-gold"
                        >
                            Generate another →
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            data-testid="export-panel"
            className="border border-brand-hairline bg-brand-surface"
        >
            {/* Mode tabs */}
            <div className="flex border-b border-brand-hairline" role="tablist">
                {[
                    { key: 'spec', label: 'Field Spec Report', icon: FileText, testid: 'export-tab-spec' },
                    { key: 'quote', label: 'Estimated Quote', icon: Receipt, testid: 'export-tab-quote' },
                ].map((t) => {
                    const Icon = t.icon;
                    const active = mode === t.key;
                    return (
                        <button
                            key={t.key}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            data-testid={t.testid}
                            onClick={() => setMode(t.key)}
                            className={[
                                'flex-1 inline-flex items-center justify-center gap-2 px-4 py-4 font-display text-xs font-bold uppercase tracking-[0.16em] transition-colors',
                                active
                                    ? 'text-brand-gold border-b-2 border-brand-gold -mb-px bg-brand-elevated'
                                    : 'text-brand-muted hover:text-brand-text',
                            ].join(' ')}
                        >
                            <Icon size={14} /> {t.label}
                        </button>
                    );
                })}
            </div>

            <form onSubmit={onSubmit} className="p-6 md:p-8">
                <p className="font-sans text-sm text-brand-muted">{modeCopy}</p>

                <div className="mt-6">
                    <label
                        htmlFor="export-project"
                        className="block font-display text-[10px] font-bold uppercase tracking-[0.18em] text-brand-gold"
                    >
                        Project Name <span className="text-brand-danger">*</span>
                    </label>
                    <input
                        id="export-project"
                        data-testid="export-project-name"
                        type="text"
                        required
                        value={projectName}
                        onChange={(e) => setProjectName(e.target.value)}
                        placeholder="e.g. Fort Nelson Access Road"
                        className="mt-3 w-full border border-brand-hairline bg-brand-elevated px-4 py-3 font-sans text-sm text-brand-text outline-none transition-colors focus:border-brand-gold"
                    />
                </div>

                {/* Optional contact toggle moved to the footer row below
                    so the toggle text and the gold download button never
                    collide at wide viewports. */}

                {includeContact && (
                    <div
                        data-testid="export-contact-fields"
                        className="mt-5 grid gap-4 md:grid-cols-2"
                    >
                        {[
                            { k: 'company', l: 'Company', v: company, set: setCompany },
                            { k: 'contact_name', l: 'Your Name', v: contactName, set: setContactName },
                            { k: 'email', l: 'Email', v: email, set: setEmail, type: 'email' },
                            { k: 'phone', l: 'Phone', v: phone, set: setPhone },
                            { k: 'location', l: 'Project Location', v: location, set: setLocation },
                        ].map((f) => (
                            <div key={f.k}>
                                <label className="block font-display text-[10px] font-bold uppercase tracking-[0.18em] text-brand-muted">
                                    {f.l}
                                </label>
                                <input
                                    type={f.type || 'text'}
                                    data-testid={`export-${f.k}`}
                                    value={f.v}
                                    onChange={(e) => f.set(e.target.value)}
                                    className="mt-2 w-full border border-brand-hairline bg-brand-elevated px-4 py-3 font-sans text-sm text-brand-text outline-none focus:border-brand-gold"
                                />
                            </div>
                        ))}
                        <div className="md:col-span-2">
                            <label className="block font-display text-[10px] font-bold uppercase tracking-[0.18em] text-brand-muted">
                                Notes
                            </label>
                            <textarea
                                data-testid="export-notes"
                                rows={3}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                className="mt-2 w-full border border-brand-hairline bg-brand-elevated px-4 py-3 font-sans text-sm text-brand-text outline-none focus:border-brand-gold"
                            />
                        </div>
                    </div>
                )}

                {status === 'error' && errMsg && (
                    <p
                        data-testid="export-error"
                        className="mt-5 font-sans text-xs text-brand-danger"
                    >
                        {errMsg}
                    </p>
                )}

                <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
                    <button
                        type="button"
                        data-testid="export-toggle-contact"
                        onClick={() => setIncludeContact((v) => !v)}
                        className="inline-flex min-w-0 flex-1 items-start gap-2 text-left font-sans text-xs text-brand-muted transition-colors hover:text-brand-blue"
                    >
                        <ChevronDown
                            size={14}
                            className={[
                                'mt-0.5 shrink-0 transition-transform duration-200',
                                includeContact ? 'rotate-180' : '',
                            ].join(' ')}
                        />
                        <span className="whitespace-normal break-words leading-snug">
                            Include contact info (optional - lets our sales team follow up)
                        </span>
                    </button>

                    <button
                        type="submit"
                        data-testid="export-submit"
                        disabled={status === 'submitting'}
                        className="inline-flex shrink-0 items-center justify-center gap-2 self-stretch bg-brand-gold px-6 py-4 font-display text-xs font-bold uppercase tracking-[0.16em] text-brand-base transition-colors duration-150 hover:bg-[#E5B647] disabled:opacity-60 sm:self-auto"
                    >
                        {status === 'submitting' ? (
                            <>
                                <Loader2 className="animate-spin" size={14} /> Generating…
                            </>
                        ) : (
                            <>Download {mode === 'spec' ? 'Field Spec Report' : 'Estimated Quote'} (PDF)</>
                        )}
                    </button>
                </div>

                <p className="mt-4 font-sans text-xs text-brand-muted">
                    No email required. Download is instant - Manufacturer Direct from
                    Chemainus, BC + Calgary, AB.
                </p>
            </form>
        </div>
    );
};

export default ExportPanel;
